import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import AdminDashboardLayout from '../../components/layout/AdminDashboardLayout';
import DataTable from '../../components/common/DataTable';
import usePageTitle from '../../hooks/usePageTitle';
import useSmartRefresh from '../../hooks/useSmartRefresh';
import walletService from '../../services/walletService';
import { SummaryCard, RefreshButton } from '../../components/ui';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

/**
 * Credit Management Page - Monitor user wallets and transactions
 */
const CreditManagementPage = () => {
  // Set page title
  usePageTitle('Credit Management');

  const [activeTab, setActiveTab] = useState('wallets');
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Credit Adjustment Modal State
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [creditData, setCreditData] = useState({
    amount: '',
    description: 'Manual adjustment by Admin',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch data
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [walletsRes, transactionsRes] = await Promise.all([
        walletService.getAllWallets(),
        walletService.getAllTransactions(),
      ]);

      if (walletsRes.success) {
        setWallets(walletsRes.wallets || []);
      }
      if (transactionsRes.success) {
        setTransactions(transactionsRes.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching credit data:', error);
      toast.error('Failed to load credit management data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const { isRefreshing, refreshData } = useSmartRefresh(
    fetchData,
    'Credit data refreshed successfully',
  );

  // Statistics
  const totalSystemBalance = wallets.reduce(
    (sum, w) => sum + parseFloat(w.balance),
    0,
  );
  const totalTransactions = transactions.length;

  // Handle Credit Adjustment
  const handleAdjustCredit = async (e) => {
    e.preventDefault();
    if (!selectedWallet || !selectedWallet.user || !selectedWallet.user.id) {
      toast.error('Invalid wallet selected');
      return;
    }
    if (!creditData.amount || isNaN(parseFloat(creditData.amount))) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await walletService.adjustCredit(
        selectedWallet.user.id,
        creditData.amount,
        creditData.description,
      );

      if (response.success) {
        toast.success(response.message || 'Credit adjusted successfully');
        setIsCreditModalOpen(false);
        setCreditData({
          amount: '',
          description: 'Manual adjustment by Admin',
        });
        fetchData();
      } else {
        toast.error(response.message || 'Failed to adjust credit');
      }
    } catch (error) {
      console.error('Error adjusting credit:', error);
      toast.error('Failed to adjust credit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreditModal = (wallet) => {
    setSelectedWallet(wallet);
    setIsCreditModalOpen(true);
  };

  // Wallets Table Columns
  const walletColumns = [
    {
      id: 'user',
      header: 'User',
      accessor: 'user',
      render: (user) => {
        if (!user || !user.email) {
          return <span className="text-gray-400">Unknown User</span>;
        }
        return (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
              {user.email.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {user.name || 'N/A'}
              </div>
              <div className="text-xs text-gray-500">{user.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'balance',
      header: 'Balance',
      accessor: 'balance',
      render: (balance) => (
        <span className="font-bold text-gray-900 dark:text-gray-100">
          $
          {parseFloat(balance || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      accessor: 'updated_at',
      render: (date) => {
        if (!date) return <span className="text-gray-400">N/A</span>;
        return (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {new Date(date).toLocaleString()}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      render: (_, wallet) => (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => openCreditModal(wallet)}
            className="flex items-center"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v12m-3-3l3 3 3-3"
              />
            </svg>
            Adjust
          </Button>
        </div>
      ),
    },
  ];

  // Transactions Table Columns
  const transactionColumns = [
    {
      id: 'created_at',
      header: 'Date',
      accessor: 'created_at',
      render: (date) => {
        if (!date) return <span className="text-gray-400">N/A</span>;
        return (
          <div className="text-sm">
            <div className="text-gray-900 dark:text-gray-100">
              {new Date(date).toLocaleDateString()}
            </div>
            <div className="text-xs text-gray-500">
              {new Date(date).toLocaleTimeString()}
            </div>
          </div>
        );
      },
    },
    {
      id: 'user_email',
      header: 'User',
      accessor: 'user_email',
      render: (email) => (
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {email || 'Unknown'}
        </div>
      ),
    },
    {
      id: 'transaction_type',
      header: 'Type',
      accessor: 'transaction_type',
      render: (type) => {
        if (!type) return <span className="text-gray-400">N/A</span>;
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              type === 'credit'
                ? 'bg-success-50 text-success-700 border border-success-100'
                : 'bg-error-50 text-error-700 border border-error-100'
            }`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </span>
        );
      },
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: 'amount',
      render: (amount, row) => {
        const amt = parseFloat(amount || 0);
        const type = row?.transaction_type || 'debit';
        return (
          <span
            className={`font-bold ${type === 'credit' ? 'text-success-600' : 'text-error-600'}`}
          >
            {type === 'credit' ? '+' : '-'}${amt.toFixed(2)}
          </span>
        );
      },
    },
    {
      id: 'description',
      header: 'Description',
      accessor: 'description',
      render: (desc) => (
        <span className="text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate block">
          {desc || 'No description'}
        </span>
      ),
    },
    {
      id: 'created_by_name',
      header: 'By',
      accessor: 'created_by_name',
      render: (name) => (
        <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-gray-500">
          {name || 'System'}
        </span>
      ),
    },
  ];

  const stats = [
    {
      label: 'System total balance',
      value: `$${totalSystemBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      bgGradient: 'from-blue-50 to-indigo-50',
    },
    {
      label: 'Active wallets',
      value: wallets.length.toString(),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      ),
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      bgGradient: 'from-indigo-50 to-purple-50',
    },
    {
      label: 'Recent transactions',
      value: totalTransactions.toString(),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      ),
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      bgGradient: 'from-emerald-50 to-teal-50',
    },
    {
      label: 'Avg user balance',
      value: `$${(wallets.length ? totalSystemBalance / wallets.length : 0).toFixed(2)}`,
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        </svg>
      ),
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      bgGradient: 'from-amber-50 to-orange-50',
    },
  ];

  return (
    <AdminDashboardLayout pageName="Credit Management">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Manage user credits, monitor balances, and track all financial
              transactions
            </p>
          </div>
          <RefreshButton onRefresh={refreshData} isRefreshing={isRefreshing} />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <SummaryCard
              key={idx}
              isLoading={isLoading}
              label={stat.label}
              value={stat.value}
              icon={stat.icon}
              iconBgColor={stat.iconBg}
              iconColor={stat.iconColor}
              className={`bg-gradient-to-br ${stat.bgGradient} dark:from-gray-800 dark:to-gray-900 border-none`}
            />
          ))}
        </div>

        {/* Main Content Sections */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('wallets')}
              className={`px-8 py-4 text-sm font-semibold transition-colors relative ${
                activeTab === 'wallets'
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              User Wallets
              {activeTab === 'wallets' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400"
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-8 py-4 text-sm font-semibold transition-colors relative ${
                activeTab === 'transactions'
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Transaction History
              {activeTab === 'transactions' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400"
                />
              )}
            </button>
          </div>

          <div className="p-1">
            <AnimatePresence mode="wait">
              {activeTab === 'wallets' ? (
                <motion.div
                  key="wallets"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <DataTable
                    columns={walletColumns}
                    data={wallets}
                    isLoading={isLoading}
                    searchPlaceholder="Search wallets by email or name..."
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="transactions"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <DataTable
                    columns={transactionColumns}
                    data={transactions}
                    isLoading={isLoading}
                    searchPlaceholder="Search transactions..."
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Credit Adjustment Modal */}
      <AnimatePresence>
        {isCreditModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-gray-100 dark:border-gray-700"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    Adjust Credits
                  </h3>
                  <button
                    onClick={() => setIsCreditModalOpen(false)}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <svg
                      className="w-5 h-5 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {selectedWallet && selectedWallet.user && (
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold">
                        {selectedWallet.user.email?.charAt(0).toUpperCase() ||
                          'U'}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {selectedWallet.user.name || 'User'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {selectedWallet.user.email || 'No email'}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Current Balance
                      </span>
                      <span className="font-bold text-gray-900 dark:text-gray-100">
                        ${parseFloat(selectedWallet.balance || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleAdjustCredit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Amount (positive to add, negative to deduct)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g. 10.00 or -5.00"
                      value={creditData.amount}
                      onChange={(e) =>
                        setCreditData({ ...creditData, amount: e.target.value })
                      }
                      required
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Reason / Description
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g. Compensation for failed check"
                      value={creditData.description}
                      onChange={(e) =>
                        setCreditData({
                          ...creditData,
                          description: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="pt-4 flex space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      fullWidth
                      onClick={() => setIsCreditModalOpen(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      fullWidth
                      isLoading={isSubmitting}
                    >
                      Process Adjustment
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AdminDashboardLayout>
  );
};

export default CreditManagementPage;
